/**
 * FinanceApp — MÓDULO FINANCEIRO (StormFin) nativo no V3.
 *
 * Montado em /financeiro com wouter `nest` → as rotas internas são relativas
 * (/importar, /lancamentos, /kpis, /fechamento) e `~/x` navega absoluto (sai do
 * módulo de volta ao ERP). Envolve tudo no `.sf-root` (CSS escopado do StormFin,
 * preserva o visual atual) + MonthProvider + Layout (TopBar/Sidebar).
 *
 * F-finance-viewer-access — acesso a super_admin (total) OU papel `viewer`
 * (sócio, somente-leitura). Admin de empresa e user comum NÃO acessam. Faz o
 * guard aqui (além do ProtectedRoute) para que os redirects usem caminho
 * absoluto `~` e não fiquem relativos ao nest. As ESCRITAS ficam bloqueadas no
 * backend (superAdminProcedure); a UI esconde os botões p/ quem não pode editar.
 */
import { Redirect, Route, Switch } from "wouter";
import { useAuth } from "../../hooks/useAuth.js";
import { useT } from "../../i18n/index.js";
import "./finance.css";
import { FechamentoPage } from "./Fechamento.js";
import { ImportPage } from "./Import.js";
import { KpisPage } from "./Kpis.js";
import { LancamentosPage } from "./Lancamentos.js";
import { Layout } from "./Layout.js";
import { FinanceAccessProvider } from "./finance-access.js";
import { MonthProvider } from "./finance-month.js";

export default function FinanceApp() {
  const { user, isLoading } = useAuth();
  const t = useT();

  if (isLoading) {
    return (
      <div
        className="sf-root"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          color: "var(--text3)",
          fontFamily: "var(--mono)",
          fontSize: 13,
        }}
      >
        {t("financeApp.loading")}
      </div>
    );
  }

  if (!user) return <Redirect to="~/login" />;
  const isViewer = user.memberships.some((m) => m.active && m.role === "viewer");
  if (!user.isSuperAdmin && !isViewer) return <Redirect to="~/dashboard" />;
  // Só super admin edita; viewer (sócio) é somente-leitura.
  const canEdit = user.isSuperAdmin;

  return (
    <div className="sf-root">
      <FinanceAccessProvider canEdit={canEdit}>
        <MonthProvider>
          <Layout>
            <Switch>
              {/* Importar é fluxo de ESCRITA — só quem pode editar (super admin). */}
              {canEdit && <Route path="/importar" component={ImportPage} />}
              <Route path="/lancamentos" component={LancamentosPage} />
              <Route path="/kpis" component={KpisPage} />
              <Route path="/fechamento" component={FechamentoPage} />
              {/* Viewer cai no dashboard (KPIs); super admin mantém o Importar. */}
              <Route>{() => <Redirect to={canEdit ? "/importar" : "/kpis"} />}</Route>
            </Switch>
          </Layout>
        </MonthProvider>
      </FinanceAccessProvider>
    </div>
  );
}
